import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ArrowRight, Sparkles } from 'lucide-react';

/**
 * MegaMenu — Linear/Stripe/Vercel-style flyout for the top navbar.
 *
 * Props:
 *   label          — button label (e.g. "Optimize")
 *   Icon           — lucide icon for the button
 *   featured       — array of 2-3 hero items with rich layout
 *                    [{ path, label, description, icon, gradient, badge? }]
 *   columns        — array of grouped link columns
 *                    [{ heading, items: [{ path, label, icon, badge?, description? }] }]
 *   footer         — optional CTA at bottom (e.g. "See all 30 tools →")
 *   accentColor    — 'emerald' | 'violet' | 'blue' | 'amber'
 *
 * Behavior:
 *   - Opens on click; closes on outside click or Escape
 *   - Active state if any of its links match current location
 *   - Closes when any link is clicked
 *   - Mobile-friendly (collapses to mobile drawer in parent)
 *   - Optional `showNewPulse` renders a small animated pulse-dot on the
 *     trigger button to draw the eye to a newly-added top-level menu.
 */
const MegaMenu = ({ label, Icon, featured = [], columns = [], footer, accentColor = 'emerald', testId, showNewPulse = false, onTriggerClick, align = 'left' }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  // v34.41 — auto-clamp dropdown horizontally so it never overflows the viewport.
  // Was: left-anchored to trigger → wide menus on smaller screens spilled past
  // the right edge (caught by OverflowAlarm on /cost-forecasting at 1366px).
  const [leftShift, setLeftShift] = useState(0);
  const location = useLocation();

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  // v34.41 — measure dropdown vs viewport on open; nudge left if it overflows.
  // Runs on open + resize so a wide menu opening near the right edge still
  // fits inside `100vw - 12px` with no horizontal scrollbar.
  useLayoutEffect(() => {
    if (!open) { setLeftShift(0); return undefined; }
    const measure = () => {
      const el = dropdownRef.current;
      if (!el) return;
      // Reset shift first so we measure the natural position.
      el.style.transform = '';
      const rect = el.getBoundingClientRect();
      const overflowRight = rect.right - (window.innerWidth - 12);
      if (overflowRight > 0) {
        setLeftShift(Math.ceil(overflowRight));
      } else {
        setLeftShift(0);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open]);

  // Active state if any link in the menu matches current path
  const allPaths = [
    ...featured.map((f) => f.path),
    ...columns.flatMap((c) => c.items.map((i) => i.path)),
  ];
  const isActive = allPaths.some((p) => location.pathname === p);

  // Brand rule (Feb 2026): emerald is the default. Amber is allowed ONLY
  // where it has a cost-context (e.g. Pilot uses ShieldCheck w/ amber). Old
  // 'violet' / 'cyan' / 'blue' callsites still work but render emerald —
  // they used to leak rogue colors into the brand.
  const _emeraldPalette = {
    activeBg: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    hoverBg: 'hover:bg-slate-100 dark:hover:bg-slate-800',
    featuredGradient: 'from-emerald-500 to-emerald-600',
    pillText: 'text-emerald-600 dark:text-emerald-400',
    pillBg: 'bg-emerald-500/10 border border-emerald-500/20',
    triggerIcon: 'text-emerald-600 dark:text-emerald-400',
  };
  const _amberPalette = {
    activeBg: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    hoverBg: 'hover:bg-slate-100 dark:hover:bg-slate-800',
    featuredGradient: 'from-amber-500 to-orange-500',
    pillText: 'text-amber-600 dark:text-amber-300',
    pillBg: 'bg-amber-500/10 border border-amber-500/30',
    triggerIcon: 'text-amber-600 dark:text-amber-300', // strong contrast, never pale
  };
  const accentClasses = {
    emerald: _emeraldPalette,
    violet: _emeraldPalette,
    cyan: _emeraldPalette,
    blue: _emeraldPalette,
    amber: _amberPalette,
  }[accentColor] || _emeraldPalette;

  // v35.67 (Jul 2026) — founder request: colorful menu. Column icon tiles
  // cycle through a vibrant 6-tint palette; badges get distinct colors per
  // type (NEW=emerald, POPULAR=amber, WORLD-FIRST=violet gradient).
  const ICON_TINTS = [
    'mm-tint-emerald bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 group-hover:bg-emerald-500 group-hover:shadow-emerald-500/40',
    'mm-tint-blue bg-blue-500/15 text-blue-600 dark:text-blue-300 group-hover:bg-blue-500 group-hover:shadow-blue-500/40',
    'mm-tint-amber bg-amber-500/15 text-amber-600 dark:text-amber-300 group-hover:bg-amber-500 group-hover:shadow-amber-500/40',
    'mm-tint-rose bg-rose-500/15 text-rose-600 dark:text-rose-300 group-hover:bg-rose-500 group-hover:shadow-rose-500/40',
    'mm-tint-cyan bg-cyan-500/15 text-cyan-600 dark:text-cyan-300 group-hover:bg-cyan-500 group-hover:shadow-cyan-500/40',
    'mm-tint-violet bg-violet-500/15 text-violet-600 dark:text-violet-300 group-hover:bg-violet-500 group-hover:shadow-violet-500/40',
  ];
  const badgeClass = (badge) => {
    const b = String(badge || '').toUpperCase();
    if (b === 'NEW') return 'mm-badge-new bg-emerald-500 text-white shadow-sm shadow-emerald-500/40';
    if (b === 'POPULAR') return 'mm-badge-popular bg-amber-500 text-white shadow-sm shadow-amber-500/40';
    if (b === 'WORLD-FIRST') return 'mm-badge-first bg-violet-500 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-sm shadow-violet-500/40';
    return `${accentClasses.pillBg} ${accentClasses.pillText}`;
  };

  // Two-tone label rendering — matches the page-title style
  // ("Cost **Accountability** Scorecard"): first word dark, key middle/last
  // word emerald, optional trailing word dark. Single-word labels stay solid.
  const _renderLabel = (raw) => {
    const parts = (raw || '').trim().split(/\s+/);
    if (parts.length <= 1) {
      return <span>{raw}</span>;
    }
    if (parts.length === 2) {
      return (
        <>
          <span>{parts[0]}</span>{' '}
          <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{parts[1]}</span>
        </>
      );
    }
    // 3+ words → dark + emerald(middle) + dark(last)
    return (
      <>
        <span>{parts[0]}</span>{' '}
        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{parts.slice(1, -1).join(' ')}</span>{' '}
        <span>{parts[parts.length - 1]}</span>
      </>
    );
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (typeof onTriggerClick === 'function') onTriggerClick();
        }}
        className={`relative flex items-center gap-1.5 px-2 xl:px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
          isActive || open
            ? accentClasses.activeBg
            : `text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white ${accentClasses.hoverBg}`
        }`}
        data-testid={testId}
        aria-expanded={open}
        title={label}
      >
        <Icon className={`w-4 h-4 ${isActive || open ? '' : accentClasses.triggerIcon}`} />
        <span className="hidden xl:inline">{_renderLabel(label)}</span>
        <ChevronDown className={`w-3 h-3 opacity-60 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        {showNewPulse && !open && (
          <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5" data-testid={`${testId}-new-pulse`}>
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 ring-2 ring-white dark:ring-slate-900"></span>
          </span>
        )}
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-2 z-50 animate-megamenu-in`}
          style={{
            minWidth: `max(640px, ${Math.max(640, 210 * Math.max(columns.length, 1))}px)`,
            maxWidth: 'calc(100vw - 24px)',
            transform: leftShift > 0 ? `translateX(-${leftShift}px)` : undefined,
          }}
        >
          <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl shadow-black/10 dark:shadow-black/40 overflow-hidden">

            {/* Featured row — gradient hero tiles for the 2-3 most important items */}
            {featured.length > 0 && (
              <div className="p-4 bg-slate-50 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-900 border-b border-slate-200 dark:border-slate-700/50">
                <p className="text-[11px] font-bold tracking-widest uppercase text-emerald-600 dark:text-emerald-400 mb-3 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  Featured
                </p>
                <div className={`grid gap-3 ${featured.length === 1 ? 'grid-cols-1' : featured.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                  {featured.map((f) => {
                    const FIcon = f.icon;
                    return (
                      <Link
                        key={f.path}
                        to={f.path}
                        onClick={() => setOpen(false)}
                        className="group relative overflow-hidden rounded-lg p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/60 hover:border-emerald-300 dark:hover:border-emerald-500/40 hover:shadow-md transition-all"
                        data-testid={`megamenu-featured-${f.path.replace('/', '')}`}
                      >
                        <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full blur-2xl bg-gradient-to-br ${f.gradient || accentClasses.featuredGradient} opacity-20 group-hover:opacity-40 transition-opacity`} />
                        <div className="relative">
                          {/* v34.99 (Feb 14 2026) — bumped icon container from w-9
                              h-9/w-4 h-4 to w-11 h-11/w-5 h-5 with a colored glow
                              shadow so the Featured cards (Anomaly Autopsy, PR Cost
                              Guardrail, Roadmap-Aware Forecast) pop in the Pilot
                              mega-menu instead of looking pale and dim. */}
                          <div className={`inline-flex w-11 h-11 rounded-xl bg-gradient-to-br ${f.gradient || accentClasses.featuredGradient} items-center justify-center shadow-lg shadow-emerald-500/30 mb-2.5 ring-1 ring-white/20`}>
                            <FIcon className="w-5 h-5 text-white drop-shadow-sm" strokeWidth={2.4} />
                          </div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">{f.label}</p>
                          {f.description && (
                            <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 line-clamp-2 leading-snug">
                              {f.description}
                            </p>
                          )}
                          {f.badge && (
                            <span className={`inline-block mt-1.5 text-[11px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded ${badgeClass(f.badge)}`}>
                              {f.badge}
                            </span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Grouped column layout — caps at 4 cols on desktop, wraps cleanly to next row when more */}
            {columns.length > 0 && (
              <div
                className="p-4 grid gap-x-6 gap-y-4"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(columns.length, 4)}, minmax(180px, 1fr))`,
                }}
              >
                {columns.map((col) => (
                  <div key={col.heading} className="min-w-0">
                    <p className="text-[11px] font-bold tracking-widest uppercase text-emerald-600 dark:text-emerald-400 mb-2">
                      {col.heading}
                    </p>
                    <ul className="space-y-0.5">
                      {col.items.map((it, itIdx) => {
                        const IIcon = it.icon;
                        const itActive = location.pathname === it.path;
                        return (
                          <li key={it.path}>
                            <Link
                              to={it.path}
                              onClick={() => setOpen(false)}
                              className={`group flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors ${
                                itActive
                                  ? `${accentClasses.activeBg}`
                                  : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-white'
                              }`}
                              data-testid={`megamenu-link-${it.path.replace('/', '')}`}
                            >
                              {/* v35.67 (Jul 2026) — founder wants COLOR: each icon
                                  square cycles through a vibrant 6-tint palette so
                                  the menu reads playful instead of monotone. */}
                              {IIcon && (
                                <span className={`mm-icon-tile inline-flex items-center justify-center w-7 h-7 rounded-md shrink-0 transition-colors ${
                                  itActive
                                    ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-500/40'
                                    : `${ICON_TINTS[itIdx % ICON_TINTS.length]} group-hover:text-white group-hover:shadow`
                                }`}>
                                  <IIcon className="w-4 h-4" strokeWidth={2.2} />
                                </span>
                              )}
                              <span className="truncate flex-1">{it.label}</span>
                              {it.badge && (
                                <span className={`text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded shrink-0 ${badgeClass(it.badge)}`}>
                                  {it.badge}
                                </span>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Footer CTA */}
            {footer && (
              <Link
                to={footer.path}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
              >
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                  {footer.label}
                </span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all" />
              </Link>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes megamenu-in {
          from { opacity: 0; transform: translateY(-4px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-megamenu-in {
          animation: megamenu-in 0.18s cubic-bezier(0.22, 1, 0.36, 1);
          transform-origin: top left;
        }
      `}</style>
    </div>
  );
};

export default MegaMenu;
